import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Lock, TrendingUp } from 'lucide-react';
import QRCodeStyling from 'qr-code-styling';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { QRTemplate } from '@/hooks/useTemplates';

interface TemplateCardProps {
  template: QRTemplate;
  onUse: (template: QRTemplate) => void;
  isPremiumUser?: boolean;
}

export function TemplateCard({ template, onUse, isPremiumUser = false }: TemplateCardProps) {
  const qrRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const settings = template.design_settings;
    
    const qrCode = new QRCodeStyling({
      width: 120,
      height: 120,
      data: 'https://example.com',
      dotsOptions: {
        color: settings.dotsColor || '#1a1a2e',
        type: settings.dotsType || 'rounded',
      },
      backgroundOptions: {
        color: settings.backgroundColor || '#ffffff',
      },
      cornersSquareOptions: {
        type: settings.cornersSquareType || 'extra-rounded',
        color: settings.cornersSquareColor || settings.dotsColor || '#1a1a2e',
      },
      cornersDotOptions: {
        type: settings.cornersDotType || 'dot',
        color: settings.cornersDotColor || settings.dotsColor || '#1a1a2e',
      },
    });

    if (qrRef.current) {
      qrRef.current.innerHTML = '';
      qrCode.append(qrRef.current);
    }
  }, [template]);

  const isLocked = template.is_premium && !isPremiumUser;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
    >
      <Card className="overflow-hidden group hover:shadow-lg transition-all">
        <CardContent className="p-4">
          {/* QR Preview */}
          <div className="relative flex items-center justify-center mb-4 p-4 rounded-lg bg-muted/30">
            <div 
              ref={qrRef} 
              className={`rounded-lg overflow-hidden ${isLocked ? 'blur-sm' : ''}`}
            />
            
            {isLocked && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-background/90 rounded-full p-3">
                  <Lock className="h-6 w-6 text-muted-foreground" />
                </div>
              </div>
            )}
            
            {template.is_premium && (
              <Badge 
                className="absolute top-2 right-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0"
              >
                Premium
              </Badge>
            )}
          </div>

          {/* Template Info */}
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-sm line-clamp-1">{template.name}</h3>
            </div>
            
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                {template.category}
              </Badge>
              {template.usage_count > 0 && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <TrendingUp className="h-3 w-3" />
                  {template.usage_count}
                </span>
              )}
            </div>
            
            {template.description && (
              <p className="text-xs text-muted-foreground line-clamp-2">
                {template.description}
              </p>
            )}
          </div>

          {/* Action Button */}
          <Button
            onClick={() => onUse(template)}
            disabled={isLocked}
            className="w-full mt-4"
            variant={isLocked ? 'outline' : 'default'}
            size="sm"
          >
            {isLocked ? 'Upgrade to Use' : 'Use Template'}
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
}
